'use client'

import Image from 'next/image'
import React, { useMemo, useState } from 'react'

import { CategoryFilterNav } from '@/components/CategoryFilterNav'
import { containerClassName } from '@/components/Container'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import RichText from '@/components/RichText'
import { amenityIconComponents } from '@/lib/amenities/amenityIconOptions'
import type { ApartmentAmenityDetail } from '@/lib/apartments/apartmentsQuery'
import { useMessages } from '@/lib/i18n/useMessages'
import { hasLexicalContent } from '@/lib/richtext/lexicalToPlainText'
import { cn } from '@/utilities/ui'

type AmenityFilter = 'all' | ApartmentAmenityDetail['category']

type RoomAmenitiesSectionProps = {
  amenities: ApartmentAmenityDetail[]
}

function RoomAmenityCard({ amenity }: { amenity: ApartmentAmenityDetail }) {
  const Icon = amenity.icon ? amenityIconComponents[amenity.icon] : null

  return (
    <article className="overflow-hidden rounded-xl border border-black/10 bg-white/65 p-5">
      <div className="flex items-center gap-3">
        {Icon ? <Icon aria-hidden className="size-5 shrink-0 text-[#0B765A]" /> : null}
        <h3 className="font-outfit text-lg font-medium leading-6 text-dark-800">{amenity.title}</h3>
      </div>

      {amenity.description && hasLexicalContent(amenity.description) ? (
        <RichText
          className="mt-3 font-outfit text-base leading-6 text-dark-800/75 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5"
          data={amenity.description}
          enableGutter={false}
          enableProse={false}
        />
      ) : null}

      {amenity.image ? (
        <div className="relative mt-4 h-full w-full aspect-16/9 overflow-hidden rounded-lg">
          <Image
            alt={amenity.image.alt}
            className="object-cover aspect-16/9"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            src={amenity.image.src}
          />
        </div>
      ) : null}
    </article>
  )
}

export function RoomAmenitiesSection({ amenities }: RoomAmenitiesSectionProps) {
  const copy = useMessages().roomDetail.amenities
  const [activeFilter, setActiveFilter] = useState<AmenityFilter>('all')

  const filteredAmenities = useMemo(
    () =>
      activeFilter === 'all'
        ? amenities
        : amenities.filter((amenity) => amenity.category === activeFilter),
    [activeFilter, amenities],
  )

  if (!amenities.length) {
    return null
  }

  return (
    <section aria-labelledby="room-amenities-heading" className="bg-cream">
      <div className={cn(containerClassName, 'pb-16 pt-8 md:pb-20 md:pt-10 lg:pb-24 lg:pt-12')}>
        <div className="max-w-[55rem]">
          <p className="font-outfit text-base font-medium text-dark-800/70">{copy.eyebrow}</p>
          <ColorTrailText
            as="h2"
            className="mt-3 font-display text-[2.5rem] font-semibold leading-tight tracking-[-0.04em] text-bluetext md:text-[4rem]"
            id="room-amenities-heading"
            segments={[
              {
                finalColor: 'var(--brand-bluetext)',
                text: copy.title,
              },
            ]}
          />
          <p className="mt-6 max-w-2xl font-outfit text-base leading-6 text-dark-800/75">
            {copy.intro}
          </p>

          <CategoryFilterNav
            activeCategory={activeFilter}
            ariaLabel={copy.filterAriaLabel}
            categoryLabels={{
              all: copy.filters.all,
              included: copy.filters.included,
              onDemand: copy.filters.onDemand,
            }}
            categoryOrder={['all', 'included', 'onDemand']}
            className="mt-8"
            desktopAlign="start"
            desktopWidth="fit"
            layoutId="room-amenity-filter-indicator"
            onSelect={(filter) => setActiveFilter(filter as AmenityFilter)}
          />
        </div>

        <ul className="mt-10 columns-1 gap-4 md:columns-2 lg:columns-3">
          {filteredAmenities.map((amenity) => (
            <li key={amenity.id} className="mb-4 break-inside-avoid">
              <RoomAmenityCard amenity={amenity} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
